import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CreditCard,
  Trash2,
  User,
  Mail,
  Calendar,
  Hash,
  Activity,
  MapPin,
  Phone,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { paymentMethodApi } from '../lib/api/paymentMethodApi';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';

const PaymentMethodDetailPage: React.FC = () => {
  const { paymentMethodId } = useParams<{ paymentMethodId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fetch payment method details
  const {
    data: paymentMethodDetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['paymentMethodDetails', paymentMethodId],
    queryFn: () => paymentMethodApi.adminGetPaymentMethodDetails(paymentMethodId!),
    enabled: !!paymentMethodId,
  });

  // Delete payment method mutation
  const deletePaymentMethodMutation = useMutation({
    mutationFn: () => paymentMethodApi.adminDeletePaymentMethod(paymentMethodId!),
    onSuccess: () => {
      // Invalidate and refetch payment methods list
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
      // Navigate back to payer detail page
      if (paymentMethodDetails?.PayerAccountId) {
        navigate(`/payers/${paymentMethodDetails.PayerAccountId}`);
      } else {
        navigate('/payers');
      }
    },
    onError: (error) => {
      console.error('Failed to delete payment method:', error);
    },
  });

  const handleBack = () => {
    if (paymentMethodDetails?.PayerAccountId) {
      navigate(`/payers/${paymentMethodDetails.PayerAccountId}`);
    } else {
      navigate('/payers');
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deletePaymentMethodMutation.mutate();
    setShowDeleteDialog(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'inactive':
        return 'secondary';
      case 'suspended':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getPaymentMethodTypeDisplay = (type: string) => {
    const map: Record<string, string> = {
      AMEX: 'Amex',
      DinersClub: 'Diners Club',
      CarteBancaire: 'Carte Bancaire',
      ProPayToProPay: 'ProPay to ProPay',
    };
    return map[type] ?? type;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-gray-200 rounded animate-pulse" />
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">
            Failed to load payment method details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (!paymentMethodDetails) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-yellow-800">
            Payment method not found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Payment Method Details</h1>
              <p className="text-muted-foreground">
                {paymentMethodDetails.AccountName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deletePaymentMethodMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Basic Information
              </CardTitle>
              <CardDescription>
                Core payment method details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Payment Method ID</p>
                    <p className="text-sm text-muted-foreground">{paymentMethodDetails.PaymentMethodId}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Payer Account ID</p>
                    <p className="text-sm text-muted-foreground">{paymentMethodDetails.PayerAccountId}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Payment Method Type</p>
                    <p className="text-sm text-muted-foreground">
                      {getPaymentMethodTypeDisplay(paymentMethodDetails.PaymentMethodType)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Account Name</p>
                    <p className="text-sm text-muted-foreground">{paymentMethodDetails.AccountName}</p>
                  </div>
                </div>

                {paymentMethodDetails.AccountNumber && (
                  <div className="flex items-center gap-3">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Account Number</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.AccountNumber}</p>
                    </div>
                  </div>
                )}

                {paymentMethodDetails.ExpirationDate && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Expiration Date</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.ExpirationDate}</p>
                    </div>
                  </div>
                )}

                {paymentMethodDetails.RoutingNumber && (
                  <div className="flex items-center gap-3">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Routing Number</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.RoutingNumber}</p>
                    </div>
                  </div>
                )}

                {paymentMethodDetails.AccountType && (
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Account Type</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.AccountType}</p>
                    </div>
                  </div>
                )}

                {paymentMethodDetails.Description && (
                  <div className="flex items-center gap-3">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Description</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.Description}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Status and Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Status & Settings
              </CardTitle>
              <CardDescription>
                Payment method status and configuration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <Badge variant={getStatusBadgeVariant(paymentMethodDetails.Status)}>
                      {paymentMethodDetails.Status}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Priority</p>
                    <p className="text-sm text-muted-foreground">{paymentMethodDetails.Priority}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Protected</p>
                    <Badge variant={paymentMethodDetails.Protected ? 'default' : 'secondary'}>
                      {paymentMethodDetails.Protected ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>

                {paymentMethodDetails.AccountCountryCode && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Country Code</p>
                      <p className="text-sm text-muted-foreground">{paymentMethodDetails.AccountCountryCode}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Created</p>
                    <p className="text-sm text-muted-foreground">{formatDate(paymentMethodDetails.CreatedDate)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Last Modified</p>
                    <p className="text-sm text-muted-foreground">{formatDate(paymentMethodDetails.LastModifiedDate)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billing Information */}
          {paymentMethodDetails.BillingInformation && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Billing Information
                </CardTitle>
                <CardDescription>
                  Billing address and contact information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {paymentMethodDetails.BillingInformation.Address && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Address</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.Address}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.City && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">City</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.City}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.State && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">State</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.State}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.ZipCode && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Zip Code</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.ZipCode}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.Country && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Country</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.Country}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.TelephoneNumber && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Phone Number</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.TelephoneNumber}</p>
                      </div>
                    </div>
                  )}

                  {paymentMethodDetails.BillingInformation.Email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Email</p>
                        <p className="text-sm text-muted-foreground">{paymentMethodDetails.BillingInformation.Email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Payment Method
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this payment method? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">
                  <strong>Payment Method:</strong> {paymentMethodDetails.AccountName}
                </p>
                <p className="text-sm text-red-800">
                  <strong>Type:</strong> {getPaymentMethodTypeDisplay(paymentMethodDetails.PaymentMethodType)}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={deletePaymentMethodMutation.isPending}
              >
                {deletePaymentMethodMutation.isPending ? 'Deleting...' : 'Delete Payment Method'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentMethodDetailPage;
